<template>
	<AppAutoSuggest
		:options="options"
		:active-item="activeItem"
		:auto-navigation="autoNavigation"
		:is-loading="isLoading"
		:placeholder="$t('autosuggests.quotes.placeholder')"
		@input="$emit('input', $event)"
		@load="loadOptions($event)"
	/>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from 'vue';
import type { SelectItem } from '@developers/scoro-ui';
import {
	useQuoteAutoSuggestLazyQuery,
	type Quote,
	QuoteAutoSuggestDocument,
} from '@/generated/graphql';
import { useQuote } from '../../useQuote';
import { AppAutoSuggest, AutoSuggestDefaultEmits } from '@/features/App';

export default defineComponent({
	components: { AppAutoSuggest },

	props: {
		/**
		 * Selected dropdown item to display without fetching all list
		 */
		item: {
			type: Object as PropType<
				| (Partial<Quote> &
						Pick<
							Quote,
							'id' | 'number' | 'quoteName' | 'mainContact'
						>)
				| undefined
			>,
			required: false,
			default: undefined,
		},

		autoNavigation: {
			type: Boolean,
			required: false,
			default: false,
		},
	},

	emits: AutoSuggestDefaultEmits,

	setup(props) {
		const {
			loading: isLoading,
			load,
			onResult,
		} = useQuoteAutoSuggestLazyQuery({
			quoteFiltersInput: {
				searchKeywords: '',
			},
		});

		const options = ref<SelectItem[]>([]);
		const { getDisplayName } = useQuote();

		onResult((result) => {
			if (result.data?.QuotesList) {
				options.value =
					result.data.QuotesList.map((row) => {
						return {
							id: row.id,
							title: getDisplayName(row),
							subtitle: row.mainContact?.name,
						};
					}) || [];
			}
		});

		const activeItem = computed<SelectItem | undefined>(() => {
			return props.item
				? {
						id: props.item.id,
						title: getDisplayName(props.item),
					}
				: undefined;
		});

		const loadOptions = async (searchKeywords: string) => {
			load(QuoteAutoSuggestDocument, {
				quoteFiltersInput: {
					searchKeywords,
				},
			});
		};

		return {
			activeItem,
			options,
			isLoading,
			loadOptions,
		};
	},
});
</script>
